/**
 * RoleSelector Component
 * 
 * Displays role options (Customer, Capture, Cashier) for device authentication.
 * Shows role descriptions and icons to help users select the appropriate role.
 */

import type { DeviceRole } from '../../../lib/auth/storage';

interface RoleSelectorProps {
  onSelect: (role: DeviceRole) => void;
}

interface RoleOption {
  role: DeviceRole;
  title: string;
  description: string;
  icon: string;
  gradient: string;
  iconBg: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    role: 'customer',
    title: 'Customer',
    description: 'Self-service device for customers to order products and edit photos',
    icon: '🛒',
    gradient: 'from-pink-500 to-purple-500',
    iconBg: 'from-pink-400 to-purple-400',
  },
  {
    role: 'capture',
    title: 'Capture',
    description: 'Photo booth camera device for capturing customer photos',
    icon: '📸',
    gradient: 'from-cyan-400 to-blue-500',
    iconBg: 'from-cyan-300 to-blue-400',
  },
  {
    role: 'cashier',
    title: 'Cashier',
    description: 'Employee device for managing transactions and outlet operations',
    icon: '💰',
    gradient: 'from-green-400 to-emerald-500',
    iconBg: 'from-green-300 to-emerald-400',
  },
];

export function RoleSelector({ onSelect }: RoleSelectorProps) {
  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">
        Select Device Role
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Choose the function for this device
      </p>
      
      <div className="grid grid-cols-1 gap-4">
        {ROLE_OPTIONS.map((option) => (
          <button
            key={option.role}
            onClick={() => onSelect(option.role)}
            className="group relative overflow-hidden rounded-2xl bg-white border-2 border-gray-200 p-6 text-left shadow-md hover:shadow-xl hover:border-purple-400 transition-all duration-300 hover:scale-[1.02]"
          >
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${option.iconBg} rounded-xl flex items-center justify-center text-3xl shadow-md`}>
                {option.icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-purple-600 transition-colors">
                  {option.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {option.description}
                </p>
              </div>
              
              <div className="flex-shrink-0 text-gray-400 group-hover:text-purple-500 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            <div className={`absolute inset-0 bg-gradient-to-r ${option.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />
          </button>
        ))}
      </div>
      
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>💡 This setting determines which features are available on this device</p>
      </div>
    </div>
  );
}
